/**
 * ============================================================================
 * LAYOUT COMPONENT: Hope UI Styled Navigation Sidebar (Digiforest WhatsApp)
 * ============================================================================
 * Purpose:
 *   Faithful implementation of the Hope UI sidebar active and hover background styling:
 *   - Rounded pill background (`rounded-lg mx-3.5 px-4 py-2.5`)
 *   - Royal blue solid background (`bg-[#3a57e8]`)
 *   - Soft glowing drop shadow (`shadow-md shadow-[#3a57e8]/30`)
 *   - Light blue hover background (`hover:bg-[#ebf0fe] hover:text-[#3a57e8]`)
 *   - All actual platform menus and routes preserved
 * ============================================================================
 */

import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getCurrentUser, getUserRole, hasPermission } from "../../core/utils/auth";
import { SERVER_BASE_URL } from "../../core/config/constants";
import {
  Squares2X2Icon,
  UsersIcon,
  KeyIcon,
  CircleStackIcon,
  SignalIcon,
  RectangleStackIcon,
  ClipboardDocumentListIcon,
  GlobeAmericasIcon,
  MegaphoneIcon,
  DocumentTextIcon,
  PhoneIcon,
  WalletIcon,
  ChatBubbleLeftRightIcon,
  LifebuoyIcon,
  XMarkIcon,
  PlusCircleIcon,
  ChartBarIcon,
  ChevronDownIcon,
  PaperAirplaneIcon,
  CommandLineIcon,
  LinkIcon,
  PuzzlePieceIcon,
  ChatBubbleOvalLeftEllipsisIcon,
} from "@heroicons/react/24/outline";

// Hope UI 8-pointed Star Geometric Logo SVG
const HopeStarLogo = () => (
  <svg className="w-8 h-8 text-[#3a57e8] flex-shrink-0" viewBox="0 0 30 30" fill="none">
    <path
      d="M15 0C15.8284 0 16.5 0.671573 16.5 1.5V13.5H28.5C29.3284 13.5 30 14.1716 30 15C30 15.8284 29.3284 16.5 28.5 16.5H16.5V28.5C16.5 29.3284 15.8284 30 15 30C14.1716 30 13.5 29.3284 13.5 28.5V16.5H1.5C0.671573 16.5 0 15.8284 0 15C0 14.1716 0.671573 13.5 1.5 13.5H13.5V1.5C13.5 0.671573 14.1716 0 15 0Z"
      fill="currentColor"
    />
    <path
      d="M5.454 5.454C6.04 4.868 6.99 4.868 7.575 5.454L15 12.879L22.425 5.454C23.01 4.868 23.96 4.868 24.546 5.454C25.132 6.04 25.132 6.99 24.546 7.575L17.121 15L24.546 22.425C25.132 23.01 25.132 23.96 24.546 24.546C23.96 25.132 23.01 25.132 22.425 24.546L15 17.121L7.575 24.546C6.99 25.132 6.04 25.132 5.454 24.546C4.868 23.96 4.868 23.01 5.454 22.425L12.879 15L5.454 7.575C4.868 6.99 4.868 6.04 5.454 5.454Z"
      fill="currentColor"
      fillOpacity="0.4"
    />
  </svg>
);

// Master Platform Administration Menus
const adminNavItems = [
  { label: "Users Management", route: "/admin/users", icon: UsersIcon },
  { label: "Roles & Permissions", route: "/admin/roles", icon: KeyIcon },
  { label: "Database Master", route: "/admin/databases", icon: CircleStackIcon },
  { label: "Telephony Operators", route: "/admin/operators", icon: SignalIcon },
  { label: "Phone Numbers", route: "/admin/phone-numbers", aliases: ["/user/phone-numbers", "/workspace/phone-numbers", "/phone-numbers"], icon: PhoneIcon },
  { label: "Countries & Rates", route: "/admin/countries", icon: GlobeAmericasIcon },
  { label: "Dynamic Menus", route: "/admin/menus", icon: RectangleStackIcon },
  { label: "Login Logs", route: "/admin/login-logs", icon: ClipboardDocumentListIcon },
  { label: "Wallet & Ledgers", route: "/admin/ledgers", aliases: ["/workspace/ledgers", "/ledgers"], icon: WalletIcon },
  { label: "API Documentation", route: "/admin/docs", aliases: ["/workspace/docs", "/docs", "/api-docs"], icon: CommandLineIcon },
  { label: "Tiny URLs", route: "/workspace/tiny-urls", aliases: ["/tiny-urls", "/workspace/links", "/links"], icon: LinkIcon, permission: "TINY_URL_MANAGEMENT" },
];

// Tenant Workspace Menus
const workspaceNavItems = [
  { label: "Quick Messages", route: "/workspace/general-messages", aliases: ["/general-messages"], icon: PaperAirplaneIcon },
  { label: "Campaign", route: "/workspace/campaigns", aliases: ["/campaigns", "/workspace/campaigns/broadcast", "/general-campaign"], icon: MegaphoneIcon },
  // { label: "Create Campaign", route: "/workspace/campaigns/create", aliases: ["/campaigns/create"], icon: PlusCircleIcon },
  {
    label: "Report Management",
    route: "/workspace/reports",
    aliases: ["/workspace/reports", "/reports"],
    icon: ChartBarIcon,
    subItems: [
      { label: "General Report", route: "/workspace/reports/general", aliases: ["/reports/general"] },
      { label: "Campaign Report", route: "/workspace/reports/campaigns", aliases: ["/reports/campaigns"] },
      { label: "API Report", route: "/workspace/reports/api", aliases: ["/reports/api"] },
    ],
  },
  { label: "Live Chat", route: "/workspace/chat", aliases: ["/chat"], icon: ChatBubbleOvalLeftEllipsisIcon, permission: "CHAT_MANAGEMENT" },
  { label: "Message Templates", route: "/workspace/templates", aliases: ["/templates"], icon: DocumentTextIcon },
  { label: "Contacts & Groups", route: "/workspace/contacts", aliases: ["/workspace/groups", "/contacts", "/groups"], icon: UsersIcon },
  { label: "Message Audit Logs", route: "/workspace/messages", aliases: ["/messages"], icon: ChatBubbleLeftRightIcon },
  {
    label: "Integrations",
    route: "/workspace/integrations/indiamart",
    aliases: ["/workspace/integrations", "/integrations/indiamart", "/workspace/indiamart", "/integrations"],
    icon: PuzzlePieceIcon,
    permission: "INDIAMART_INTEGRATION",
    subItems: [
      { label: "IndiaMART", route: "/workspace/integrations/indiamart", aliases: ["/integrations/indiamart", "/workspace/indiamart"] },
    ],
  },
  { label: "Tiny URLs", route: "/workspace/tiny-urls", aliases: ["/tiny-urls", "/workspace/links", "/links"], icon: LinkIcon, permission: "TINY_URL_MANAGEMENT" },
  { label: "API Documentation", route: "/workspace/docs", aliases: ["/docs", "/api-docs", "/documentation", "/workspace/api-docs", "/admin/docs"], icon: CommandLineIcon },
  { label: "Support & Alerts", route: "/workspace/tickets", aliases: ["/tickets"], icon: LifebuoyIcon },
];


const Sidebar = ({ collapsed, mobileOpen, onClose, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [openSubmenus, setOpenSubmenus] = useState({ "Report Management": true });

  const toggleSubmenu = (label) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const currentUser = getCurrentUser();
  const userRole = getUserRole(currentUser);

  // Dynamic Administration navigation items filtered by role:
  // - SuperAdmin: shows all master administration items including Wallet & Ledgers
  // - Admin: shows Users Management, Wallet & Ledgers, Phone Numbers, Countries & Rates, Login Logs
  // - Reseller: shows Users Management, Wallet & Ledgers, Phone Numbers, Countries & Rates
  // - Account: shows Wallet & Ledgers, Users Management
  // - User: shows User Details, Phone Numbers (read-only), Countries
  const visibleAdminNavItems = useMemo(() => {
    if (userRole === "superadmin") {
      return adminNavItems;
    }
    if (userRole === "admin") {
      return adminNavItems.filter((item) =>
        ["/admin/users", "/admin/ledgers", "/admin/phone-numbers", "/admin/countries", "/admin/login-logs", "/admin/docs"].includes(item.route)
      );
    }
    if (userRole === "reseller") {
      return [
        { label: "Users Management", route: "/admin/users", icon: UsersIcon },
        { label: "Wallet & Ledgers", route: "/admin/ledgers", aliases: ["/workspace/ledgers", "/ledgers"], icon: WalletIcon },
        { label: "Phone Numbers", route: "/admin/phone-numbers", aliases: ["/user/phone-numbers", "/workspace/phone-numbers", "/phone-numbers"], icon: PhoneIcon },
        { label: "Countries & Rates", route: "/admin/countries", icon: GlobeAmericasIcon },
        { label: "API Documentation", route: "/admin/docs", aliases: ["/workspace/docs", "/docs"], icon: CommandLineIcon },
      ];
    }

    if (userRole === "account") {
      return [
        { label: "Wallet & Ledgers", route: "/admin/ledgers", aliases: ["/workspace/ledgers", "/ledgers"], icon: WalletIcon },
        { label: "Users Management", route: "/admin/users", icon: UsersIcon },
      ];
    }
    // Regular User / Client User
    return [
      { label: "User Details", route: "/admin/users", icon: UsersIcon },
      { label: "Phone Numbers", route: "/user/phone-numbers", aliases: ["/admin/phone-numbers", "/workspace/phone-numbers", "/phone-numbers"], icon: PhoneIcon },
      { label: "Countries", route: "/admin/countries", icon: GlobeAmericasIcon },
    ];
  }, [userRole]);

  // Dynamic Tenant Workspace navigation items filtered by granular permissions
  const visibleWorkspaceNavItems = useMemo(() => {
    return workspaceNavItems.filter((item) => {
      if (item.permission) {
        return hasPermission(currentUser, item.permission);
      }
      return true;
    });
  }, [currentUser]);

  const isCurrent = (route, aliases = []) => {
    if (location.pathname === route) return true;
    if (aliases && aliases.some((a) => location.pathname === a || location.pathname.startsWith(a + "/"))) {
      return true;
    }
    return false;
  };

  const handleNav = (route) => {
    navigate(route);
    if (mobileOpen && onClose) onClose();
  };

  const isDashboardActive = location.pathname === "/";

  // Reusable NavItem with fixed 85% width and 8px (approx 5%) border radius
  const renderNavItem = (item, idx) => {
    const hasSubItems = Array.isArray(item.subItems) && item.subItems.length > 0;
    const isAnySubActive = hasSubItems && item.subItems.some((sub) => isCurrent(sub.route, sub.aliases));
    const active = isCurrent(item.route, item.aliases) || isAnySubActive;
    const Icon = item.icon;
    const isOpen = openSubmenus[item.label] ?? isAnySubActive;

    if (hasSubItems) {
      return (
        <div key={idx} className="space-y-1">
          <button
            onClick={() => {
              if (collapsed) {
                handleNav(item.subItems[0].route);
              } else {
                toggleSubmenu(item.label);
              }
            }}
            title={collapsed ? item.label : undefined}
            className={`${
              collapsed
                ? "w-12 h-10 mx-auto justify-center"
                : "w-[85%] mx-auto justify-start px-3.5 py-2.5"
            } flex items-center gap-3 rounded-[8px] text-sm transition-all duration-150 cursor-pointer ${
              active
                ? "bg-[#3a57e8] text-white font-medium shadow-md shadow-[#3a57e8]/30"
                : "text-[#8a92a6] dark:text-gray-400 hover:text-[#3a57e8] dark:hover:text-white hover:bg-[#ebf0fe] dark:hover:bg-gray-800/80 font-medium"
            }`}
          >
            <Icon
              className={`w-5 h-5 flex-shrink-0 transition-colors ${
                active ? "text-white" : "text-[#8a92a6]"
              }`}
            />
            {!collapsed && (
              <>
                <span className="truncate flex-1 text-left">{item.label}</span>
                <ChevronDownIcon
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </>
            )}
          </button>

          {/* Submenu Dropdown List */}
          {!collapsed && isOpen && (
            <div className="w-[85%] mx-auto pl-6 pr-1 py-1 space-y-1 border-l-2 border-gray-100 dark:border-gray-800 ml-7">
              {item.subItems.map((sub, sIdx) => {
                const subActive = isCurrent(sub.route, sub.aliases);
                return (
                  <button
                    key={sIdx}
                    onClick={() => handleNav(sub.route)}
                    className={`flex items-center w-full px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                      subActive
                        ? "bg-[#3a57e8] text-white shadow-xs font-bold"
                        : "text-[#8a92a6] dark:text-gray-400 hover:text-[#3a57e8] dark:hover:text-white hover:bg-[#ebf0fe] dark:hover:bg-gray-800/60"
                    }`}
                  >
                    <span className="truncate">{sub.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <button
        key={idx}
        onClick={() => handleNav(item.route)}
        title={collapsed ? item.label : undefined}
        className={`${
          collapsed
            ? "w-12 h-10 mx-auto justify-center"
            : "w-[85%] mx-auto justify-start px-3.5 py-2.5"
        } flex items-center gap-3 rounded-[8px] text-sm transition-all duration-150 cursor-pointer ${
          active
            ? "bg-[#3a57e8] text-white font-medium shadow-md shadow-[#3a57e8]/30"
            : "text-[#8a92a6] dark:text-gray-400 hover:text-[#3a57e8] dark:hover:text-white hover:bg-[#ebf0fe] dark:hover:bg-gray-800/80 font-medium"
        }`}
      >
        <Icon
          className={`w-5 h-5 flex-shrink-0 transition-colors ${
            active ? "text-white" : "text-[#8a92a6]"
          }`}
        />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </button>
    );
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden backdrop-blur-xs transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Main Sidebar Aside */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50 
          ${collapsed ? "w-20" : "w-64"} 
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          bg-white dark:bg-[#151824]
          border-r border-[#e9ecef] dark:border-gray-800
          transition-all duration-300 ease-in-out
          flex flex-col shadow-sm
        `}
      >
        {/* Brand Header with Digiforest Official Logo */}
        <div className="flex items-center justify-between px-4 py-4 min-h-[68px] border-b border-[#e9ecef] dark:border-gray-800">
          <div
            className="flex items-center gap-2 cursor-pointer min-w-0 flex-1"
            onClick={() => handleNav("/")}
          >
            {collapsed ? (
              <img
                src="/assests/logo/icon.png"
                alt="Digiforest"
                className="w-8 h-8 object-contain flex-shrink-0 mx-auto"
                title="Digiforest"
              />
            ) : (
              <div className="flex items-center gap-2">
                <img
                  src="/assests/logo/logo.png"
                  alt="Digiforest"
                  className="h-8.5 w-auto max-w-[170px] object-contain dark:hidden"
                />
                <img
                  src="/assests/logo/logodark.png"
                  alt="Digiforest"
                  className="h-8.5 w-auto max-w-[170px] object-contain hidden dark:block"
                />
              </div>
            )}
          </div>

          {/* Mobile Close Button (shown only when mobile sidebar drawer is open) */}
          {mobileOpen && (
            <button
              onClick={onClose}
              className="lg:hidden p-1 text-[#8a92a6] hover:text-[#232d42] dark:hover:text-white cursor-pointer ml-2 flex-shrink-0"
              title="Close menu"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Scrollable Navigation List */}
        <div className="flex-1 py-3 overflow-y-auto space-y-4">
          {/* SECTION: HOME */}
          <div>
            {!collapsed && (
              <div className="text-xs font-bold text-[#8a92a6] dark:text-gray-400 uppercase tracking-wider w-[85%] mx-auto mb-2 px-1">
                Home
              </div>
            )}

            {/* Dashboard Link with fixed 85% width and 8px border radius */}
            <button
              onClick={() => handleNav("/")}
              title={collapsed ? "Dashboard" : undefined}
              className={`${
                collapsed
                  ? "w-12 h-10 mx-auto justify-center"
                  : "w-[85%] mx-auto justify-start px-3.5 py-2.5"
              } flex items-center gap-3 rounded-[8px] text-sm transition-all duration-150 cursor-pointer ${
                isDashboardActive
                  ? "bg-[#3a57e8] text-white font-medium shadow-md shadow-[#3a57e8]/30"
                  : "text-[#8a92a6] dark:text-gray-400 hover:text-[#3a57e8] dark:hover:text-white hover:bg-[#ebf0fe] dark:hover:bg-gray-800/80 font-medium"
              }`}
            >
              <Squares2X2Icon
                className={`w-5 h-5 flex-shrink-0 transition-colors ${
                  isDashboardActive ? "text-white" : "text-[#8a92a6]"
                }`}
              />
              {!collapsed && <span className="truncate">Dashboard</span>}
            </button>
          </div>

          {/* SECTION: ADMINISTRATION (Master Modules) */}
          <div className="pt-2">
            {!collapsed ? (
              <div className="text-xs font-bold text-[#8a92a6] dark:text-gray-400 uppercase tracking-wider w-[85%] mx-auto mb-2 px-1">
                Administration
              </div>
            ) : (
              <div className="border-t border-[#e9ecef] dark:border-gray-800 my-2" />
            )}

            <div className="space-y-1.5">
              {visibleAdminNavItems.map((item, idx) => renderNavItem(item, idx))}
            </div>
          </div>

          {/* SECTION: TENANT WORKSPACE (User Modules) */}
          <div className="pt-2">
            {!collapsed ? (
              <div className="text-xs font-bold text-[#8a92a6] dark:text-gray-400 uppercase tracking-wider w-[85%] mx-auto mb-2 px-1">
                Workspace
              </div>
            ) : (
              <div className="border-t border-[#e9ecef] dark:border-gray-800 my-2" />
            )}

            <div className="space-y-1.5">
              {visibleWorkspaceNavItems.map((item, idx) => renderNavItem(item, idx))}
            </div>
          </div>
        </div>

        {/* Sidebar Footer Live Status */}
        {!collapsed && (() => {
          const liveLabel = (() => {
            // try {
            //   const u = new URL(SERVER_BASE_URL);
            //   return u.port ? `Port ${u.port} Live` : `${u.hostname} Live`;
            // } catch (_) {
            //   return "API Live";
            // }
          })();

          return (
            <div className="p-4 border-t border-[#e9ecef] dark:border-gray-800 bg-[#f5f6fa]/60 dark:bg-gray-800/40">
              <div className="flex items-center justify-between text-xs text-[#8a92a6]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#1aa053] animate-pulse"></span>
                  <span className="font-semibold text-[#232d42] dark:text-white">
                    {liveLabel}
                  </span>
                </div>
                {/* <span className="text-[10px] font-mono text-[#8a92a6]">v2.4 Pro</span> */}
              </div>
            </div>
          );
        })()}
      </aside>
    </>
  );
};

export default Sidebar;
